<template>
  <div
    class="absolute fill-height sponsors-page"
    :class="{dragging: mousedown, 'has-page-open': slug}"
    @wheel="onMouseWheel"
    @mousemove="onMouseMove"
    @touchmove="onMouseMove"
    @mousedown="onMouseDown"
    @touchstart="onMouseDown"
    @mouseup="onMouseUp"
    @touchend="onMouseUp"
    @mouseleave="onMouseUp"
    >

    <transition>
      <!-- selector -->
      <div class="absolute fill-height selector" v-if="!slug" key="selector">
        <AnimatedClouds />
        <div class="absolute clouds" />
        <div class="absolute clouds clouds--second" />

        <transition name="thumbnail-transition">
          <div class="thumbnail-container absolute" v-show="selectedInitiative" v-if="lastSelectedInitiative && !$root.platform.mobile" :style="thumbnailStyle">
            <div class="thumbnail">
              <transition mode="out-in">
                <seo-v-img :key="lastSelectedInitiative.previewImage.id" :src="`/media/${lastSelectedInitiative.previewImage.id}`" :lazy-src="JSON.parse(lastSelectedInitiative.previewImage.metaData)['lazy-image-src']" :width="viewPort.width * 0.3" :aspect-ratio="16/9" cover />
              </transition>
            </div>
          </div>
        </transition>

        <transition name="container-transition" appear>
          <div class="scroller-container-outer fill-height absolute" v-if="initiatives">
            <div class="scroller-container fill-height absolute" >
              <div class="centered">
                <div class="row-container">
                  <div class="initiative-row" ref="row1" :style="rowStyle(1, 'row1')">
                    <router-link data-cursor-target="center center" :style="itemStyle(initiative)" exact @mouseenter.native="onMouseEnter($event, initiative)" @mouseleave.native="onMouseLeave" :to="{params: {slug: initiative._slug}}" class="item" v-for="(initiative, i) in initiatives" :key="i">{{initiative.title}}</router-link>
                  </div>
                </div>
                <div class="row-container">
                  <div class="initiative-row" ref="row2" :style="rowStyle(-1, 'row2')">
                    <router-link data-cursor-target="center center" :style="itemStyle(initiative)" exact @mouseenter.native="onMouseEnter($event, initiative)" @mouseleave.native="onMouseLeave" :to="{params: {slug: initiative._slug}}" class="item" v-for="(initiative, i) in reverseInitiatives" :key="i">{{initiative.title}}</router-link>
                  </div>
                </div>
                <div class="row-container">
                  <div class="initiative-row" ref="row3" :style="rowStyle(1, 'row3')">
                    <router-link data-cursor-target="center center" :style="itemStyle(initiative)" exact @mouseenter.native="onMouseEnter($event, initiative)" @mouseleave.native="onMouseLeave" :to="{params: {slug: initiative._slug}}" class="item" v-for="(initiative, i) in initiatives" :key="i">{{initiative.title}}</router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>

        <!-- <div class="help">{{!$root.platform.mobile ? $root.siteSettings.scrollHelpText : $root.siteSettings.clickAndDragText}}</div> -->
        <ClickDragTutorial :scroll="!$root.platform.mobile" />
      </div>
      <!-- iframe  -->
      <div class="absolute fill-height" key="iframe" v-else-if="Sponsors && slug && selectedInitiativeDisplay.iFrameURL">
        <iframe class="absolute" :src="`${selectedInitiativeDisplay.iFrameURL}?lang=${$language.current}&audio=${$root.soundManager.muteStatus.user.muted ? 0 : 1}`" />
      </div>

      <component :is="pageMapping[slug]" class="absolute fill-height" v-else-if="Sponsors && slug && selectedInitiativeDisplay.internalPageName" />

    </transition>

  </div>
</template>

<script>
import ListSponsor from '@/graphql/ListSponsor.gql'
import { normalizeWheel, ElasticNumber, RAF } from '@monogrid/js-utils'
import { Viewport } from '@monogrid/vue-lib'
import find from 'lodash/find'
import ClickDragTutorial from '@/atoms/ClickDragTutorial'
import AnimatedClouds from '@/atoms/AnimatedClouds'
// import XiaomiPage from '@/pages/sponsors/XiaomiPage'

export default {
  name: 'SponsorsPage',
  mixins: [Viewport],
  components: {
    ClickDragTutorial,
    AnimatedClouds
  },
  apollo: {
    Sponsors: {
      query: ListSponsor,
      variables () {
        return {
          sortOn: '_slug',
          status: 'PUBLISHED'
        }
      }
    }
  },

  props: {
    slug: {
      type: String,
      required: false
    }
  },

  mounted () {
    this.time = new Date().getTime()
    RAF.add(this.update)
  },

  data () {
    return {
      repeat: 10,
      selectedInitiative: null,
      lastSelectedInitiative: null,
      startX: 0,
      startElasticScroll: 0,
      mousedown: false,
      elasticScroll: new ElasticNumber(0),
      elasticMouseX: new ElasticNumber(0),
      elasticMouseY: new ElasticNumber(0),
      pageMapping: {
        xiaomi: () => import(/* webpackChunkName: "page-sponsor-xiaomi" */ './sponsors/XiaomiPage')
      }
    }
  },

  computed: {
    reverseInitiatives () {
      return this.Sponsors ? this.repeatFor(this.Sponsors.items.concat().reverse(), this.repeat) : []
    },
    initiatives () {
      return this.Sponsors ? this.repeatFor(this.Sponsors.items, this.repeat) : []
    },
    thumbnailStyle () {
      return {
        transform: `translate3d(${this.elasticMouseX.value - (this.viewPort.width / 2)}px, ${this.elasticMouseY.value - (this.viewPort.height / 2)}px, 0)`
      }
    },
    selectedInitiativeDisplay () {
      return find(this.Sponsors.items, (e) => e._slug === this.slug)
    }
  },

  watch: {
    selectedInitiative (value) {
      if (value) this.lastSelectedInitiative = value
    }
  },

  methods: {
    update () {
      const now = new Date().getTime()
      const delta = (now - this.time) / 1000
      this.elasticMouseX.update(Math.min(delta, 1 / 10))
      this.elasticMouseY.update(Math.min(delta, 1 / 10))
      this.elasticScroll.update(Math.min(delta, 1 / 10))
      this.time = now
    },
    onMouseMove (e) {
      this.elasticMouseX.target = e.touches && e.touches.length > 0 ? e.touches[0].pageX : e.pageX
      this.elasticMouseY.target = e.touches && e.touches.length > 0 ? e.touches[0].pageY : e.pageY

      if (this.mousedown) {
        const walkX = (e.touches && e.touches.length > 0 ? e.touches[0].pageX : e.pageX) - this.startX
        this.elasticScroll.target = this.startElasticScroll + walkX
      }
      if (!this.slug) {
        e.preventDefault()
      }
    },
    repeatFor (array, num) {
      let ret = []
      for (let i = 0; i < num; i++) {
        ret = ret.concat(array)
      }
      return ret
    },
    onMouseEnter (e, initiative) {
      this.selectedInitiative = initiative
    },
    onMouseLeave (e) {
      this.selectedInitiative = null
    },
    onMouseDown (e) {
      this.mousedown = true
      this.startX = e.touches && e.touches.length > 0 ? e.touches[0].pageX : e.pageX
      this.startElasticScroll = this.elasticScroll.target
    },
    onMouseUp (e) {
      this.mousedown = false
    },
    onMouseWheel (e) {
      // console.log('ok')
      this.elasticScroll.target += normalizeWheel(e).pixelY + normalizeWheel(e).pixelX
      if (!this.slug) {
        e.preventDefault()
      }
    },
    itemStyle (initiative) {
      return {
        zIndex: initiative === this.selectedInitiative ? 2 : 0
      }
    },
    rowStyle (direction = 1, ref) {
      const scroll = this.elasticScroll.value * direction
      let width = 0
      if (this.$refs[ref]) {
        width = this.$refs[ref].getBoundingClientRect().width
        // console.log(width)
        return {
          transform: `translate3d(${scroll % ((width / 2) - this.viewPort.width)}px, 0, 0)`
        }
      }
      return {
        transform: `translate3d(${scroll}px, 0, 0)`
      }
    }
  },
  metaInfo () {
    if (this.slug && this.Sponsors) {
      return {
        // home page browser title
        title: find(this.Sponsors.items, { _slug: this.slug }).shareTitle,
        meta: [
          { vmid: 'description', name: 'description', content: find(this.Sponsors.items, { _slug: this.slug })[`${this.$language.current}ShareDescription`] },
          { vmid: 'ogtitle', property: 'og:title', itemprop: 'name', content: find(this.Sponsors.items, { _slug: this.slug }).shareTitle },
          { vmid: 'ogdescription', property: 'og:description', content: find(this.Sponsors.items, { _slug: this.slug })[`${this.$language.current}ShareDescription`] },
          { vmid: 'ogimage', property: 'og:image', content: `https://${this.$root.process.env.SERVICE_TARGET_DOMAIN}/media/${find(this.Sponsors.items, { _slug: this.slug }).shareImage.id}` }
        ]
      }
    }
    const pageName = 'sponsors'
    const imageMeta = JSON.parse(this.$root.siteSettings[`${pageName}PageShareImage`].metaData)
    return {
      // home page browser title
      title: this.$root.siteSettings[`${pageName}PageTitle`],
      meta: [
        { vmid: 'description', name: 'description', content: this.$root.siteSettings[`${pageName}PageShareDescription`] },
        { vmid: 'ogtitle', property: 'og:title', itemprop: 'name', content: `${this.$root.siteSettings[`${pageName}PageTitle`]} | ${this.$root.siteSettings.siteTitle}` },
        { vmid: 'ogdescription', property: 'og:description', content: this.$root.siteSettings[`${pageName}PageShareDescription`] },
        { vmid: 'ogimage', property: 'og:image', content: `https://${this.$root.process.env.SERVICE_TARGET_DOMAIN}/media/${this.$root.siteSettings[`${pageName}PageShareImage`].id}` },
        { vmid: 'ogimagetype', property: 'og:image:type', content: this.$root.siteSettings[`${pageName}PageShareImage`].mimeType },
        { vmid: 'ogimageheight', property: 'og:image:width', content: imageMeta['image-width'] },
        { vmid: 'ogimagewidth', property: 'og:image:height', content: imageMeta['image-height'] }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>

$container-transition-lenght: 2.4s;

.sponsors-page {
  // perspective: 1000px;
  overflow: hidden;
  cursor: grab;

  &.dragging {
    cursor: grabbing;
  }

  &.has-page-open {
    cursor: initial;
  }
}

.selector {
  perspective: 1000px;
}

.absolute {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.centered {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translate3d(0, -50%, 0);
}

.row-container {
  border-bottom: 1px solid map-get(map-get($material-light, 'text'), 'secondary');
  white-space: nowrap;
  transform: translateX(-50%);

  &:last-child {
    border-bottom: 0;
  }
}

.initiative-row {
  // border-bottom: 3px solid #ccc;
  white-space: nowrap;
}

.item {
  font-size: 11vh;
  font-family: $heading-font-family;
  color: map-get(map-get($material-light, 'text'), 'secondary');
  padding: 6vh;
  // cursor: pointer;
  transition: color 0.3s $ease-in-out-cubic;

  &:hover {
    color: $c-pink;
  }
}

.thumbnail-container {
  perspective: 200px;
}

.thumbnail {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%, -50%, 0);
  z-index: 1;
}

.thumbnail-transition-enter-active,
.thumbnail-transition-leave-active {
  transition: opacity 1s $ease-in-out-quart;

  .thumbnail {
    transition: transform 1s $ease-in-out-quart;
  }
}

.thumbnail-transition-leave-to {
  opacity: 0;

  .thumbnail {
    transform: translate3d(-50%, -50%, -100px) rotateX(15deg) rotateY(-5deg);
  }
}

.thumbnail-transition-enter {
  opacity: 0;

  .thumbnail {
    transform: translate3d(-50%, -50%, -100px) rotateX(-15deg) rotateY(5deg);
  }
}

.container-transition-enter-active,
.container-transition-leave-active {
  transition: transform $container-transition-lenght $ease-in-out-cubic;

  .scroller-container {
    transition: transform $container-transition-lenght*0.6 $ease-in-out-cubic $container-transition-lenght*0.44;
  }

  .row-container {
    &:nth-child(1) {
      transition: opacity $container-transition-lenght $ease-in-out-cubic 0s;
    }

    &:nth-child(2) {
      transition: opacity $container-transition-lenght*0.9 $ease-in-out-cubic $container-transition-lenght*0.1;
    }

    &:nth-child(3) {
      transition: opacity $container-transition-lenght*0.8 $ease-in-out-cubic $container-transition-lenght*0.2;
    }
  }
}

.container-transition-enter,
.container-transition-leave-to {

  transform: translate3d(0, 0, 0) rotatez(4deg);

  .scroller-container {
    transform: translate3d(0, 50px, 0);
  }

  .row-container {
    opacity: 0;
  }
}

iframe {
  border: 0;
  padding: 0;
}

// .help {
//   position: absolute;
//   width: 100%;
//   // left: 50%;
//   bottom: 20px;
//   // transform: translateX(-50%);
//   pointer-events: none;
//   user-select: none;
//   text-align: center;
// }

.tutorial {
  position: absolute;
  bottom: 0;
  width: 100%;
}

.clouds {
  pointer-events: none;
  user-select: none;
  transform-origin: 50% 50%;
  transform: translate3d(0, -40%, 0) scale3d(2, 2, 2);

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    background-image: url('../assets/letter/clouds.webp');
    background-size: contain;
    background-position: center bottom;

    animation-name: clouds-motion-1;
    animation-fill-mode: forwards;
    animation-iteration-count: infinite;
    animation-duration: 180s;
    animation-timing-function: linear;

    .no-webp & {
      background-image: url('../assets/letter/clouds-min.png');
    }
  }

  &--second {
    transform: translate3d(0, -22%, 0) scale3d(-1.6, 1.6, 1.6);

    &::after {
      animation-name: clouds-motion-1;
      animation-fill-mode: forwards;
      animation-iteration-count: infinite;
      animation-duration: 100s;
      animation-timing-function: linear;
    }
  }
}

</style>
